import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useWatch } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';

import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { cn } from '#/lib/utils';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { Switch } from '#/modules/commonComponents/switch';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import { parseDetailAssemblyOptions } from '#/modules/imsMgmt/settings/mtr/helper/assemblyOptionsSerialize';
import { compareMtrNo } from '#/modules/momMgmt/workOrder/helper/compareMtrNo';
import { detailQueryKey } from '#/utils/queryKeys';

function mergeNonFromSelectOption(options, toMtrId, fromMtrId, label) {
  if (!toMtrId || toMtrId === fromMtrId) {
    return options;
  }
  if (options.some((option) => option.value === toMtrId)) {
    return options;
  }
  return [{ value: toMtrId, label: label || toMtrId }, ...options];
}

function SubstitutionsShell({ children }) {
  return (
    <section
      className={cn(
        'form-section @container/form-section mt-4 min-w-0',
        'border-t border-dashed border-slate-400 pt-4',
      )}
    >
      <h3 className="form-subsection__title">替代料</h3>
      <div className="form-section__body create-edit-panel-scroll min-h-16">
        {children}
      </div>
    </section>
  );
}

SubstitutionsShell.propTypes = {
  children: PropTypes.node,
};

function rowKey(row, index) {
  return String(row?.sourceGroupMtrId || `sub-${index}`);
}

function getSubstitutionRowErrorMessage(errors, index) {
  const rowErrors = errors?.mtrSubstitutions?.[index];
  if (!rowErrors || typeof rowErrors !== 'object') {
    return null;
  }
  if (rowErrors.toMtrId?.message) {
    return String(rowErrors.toMtrId.message);
  }
  if (rowErrors.message) {
    return String(rowErrors.message);
  }
  const firstField = Object.values(rowErrors).find((value) => value?.message);
  return firstField?.message ? String(firstField.message) : null;
}

const EMPTY_SUBSTITUTION_ROWS = [];

const WorkOrderAmendmentSubstitutionsEditor = ({
  control,
  register,
  setValue,
  errors,
  locked = false,
}) => {
  const watchedRows = useWatch({ control, name: 'mtrSubstitutions' });
  const rows = Array.isArray(watchedRows) ? watchedRows : EMPTY_SUBSTITUTION_ROWS;
  const groupIds = useMemo(
    () => [...new Set(rows.map((row) => row?.sourceGroupMtrId).filter(Boolean))],
    [rows],
  );

  const mtrQueries = useQueries({
    queries: groupIds.map((mtrId) => ({
      queryKey: detailQueryKey('mtr', mtrId),
      queryFn: () => fetchMtrDetail(mtrId),
      enabled: Boolean(mtrId),
    })),
  });

  const optionsByGroupId = useMemo(() => {
    const map = new Map();
    mtrQueries.forEach((query, index) => {
      const groupId = groupIds[index];
      const options = [...parseDetailAssemblyOptions(query.data?.data)]
        .sort((left, right) => compareMtrNo(left.mtrNo, right.mtrNo))
        .map((option) => ({
          value: option.optionMtrId,
          label: [option.mtrNo, option.name].filter(Boolean).join(' ')
            || option.optionMtrId,
        }));
      map.set(groupId, options);
    });
    return map;
  }, [groupIds, mtrQueries]);

  const mtrsReady = groupIds.length === 0
    || mtrQueries.every((query) => !query.isLoading && (query.isSuccess || query.isError));

  /** OFF 列手動展開；ON 列一律視為展開 */
  const [manualOpenKeys, setManualOpenKeys] = useState(() => new Set());

  useEffect(() => {
    if (!mtrsReady) {
      return;
    }
    rows.forEach((row, index) => {
      const replaceableCount = (optionsByGroupId.get(row.sourceGroupMtrId) ?? [])
        .filter((option) => option.value !== row.fromMtrId)
        .length;
      if (replaceableCount > 0 || !row?.changeEnabled) {
        return;
      }
      setValue(`mtrSubstitutions.${index}.changeEnabled`, false, {
        shouldDirty: false,
        shouldValidate: true,
      });
      setValue(`mtrSubstitutions.${index}.toMtrId`, row.fromMtrId, {
        shouldDirty: false,
        shouldValidate: true,
      });
    });
  }, [mtrsReady, optionsByGroupId, rows, setValue]);

  function isRowOpen(row, index, changeEnabled, hasRowError) {
    if (hasRowError || changeEnabled) {
      return true;
    }
    return manualOpenKeys.has(rowKey(row, index));
  }

  function toggleItemOpen(key, changeEnabled, hasRowError) {
    if (changeEnabled || hasRowError) {
      return;
    }
    setManualOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function handleChangeEnabled(index, row, checked) {
    const key = rowKey(row, index);
    setValue(`mtrSubstitutions.${index}.changeEnabled`, checked, {
      shouldDirty: true,
      shouldValidate: false,
    });

    if (checked) {
      setManualOpenKeys((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    // OFF：鎖回 from；不清空 remark；預設收合
    setValue(`mtrSubstitutions.${index}.toMtrId`, row.fromMtrId, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setManualOpenKeys((prev) => {
      if (!prev.has(key)) {
        return prev;
      }
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }

  if (rows.length === 0) {
    return (
      <SubstitutionsShell>
        <p className="text-sm text-muted-foreground">此工單無需替代料。</p>
      </SubstitutionsShell>
    );
  }

  return (
    <SubstitutionsShell>
      {locked ? (
        <p className="mb-3 text-sm text-amber-800">
          已有工單來源出庫，不可更改替代料。
        </p>
      ) : null}
      {!mtrsReady ? (
        <p className="mb-3 text-sm text-muted-foreground">載入替代料選項…</p>
      ) : null}
      <div className="divide-y divide-slate-200 rounded-md border border-gray-200 bg-panel">
        {rows.map((row, index) => {
          const groupOptions = optionsByGroupId.get(row.sourceGroupMtrId) ?? [];
          const replaceableOptions = groupOptions.filter(
            (option) => option.value !== row.fromMtrId,
          );
          const toLabel = row.toMtrId && row.toMtrId !== row.fromMtrId
            ? String(row.toMtrId)
            : '';
          const options = mergeNonFromSelectOption(
            replaceableOptions,
            row.toMtrId,
            row.fromMtrId,
            toLabel,
          );
          const cannotSubstitute = mtrsReady && replaceableOptions.length === 0;
          const changeEnabled = Boolean(row.changeEnabled) && !cannotSubstitute;
          const contentLocked = locked || !changeEnabled;
          const toChanged = Boolean(row.toMtrId) && row.toMtrId !== row.fromMtrId;
          const key = rowKey(row, index);
          const switchDisabled = locked || cannotSubstitute;
          const rowErrorMessage = getSubstitutionRowErrorMessage(errors, index);
          const hasRowError = Boolean(rowErrorMessage);
          const isOpen = isRowOpen(row, index, changeEnabled, hasRowError);

          return (
            <div
              key={key}
              className={cn(
                isOpen && 'bg-slate-50/40',
                hasRowError && 'border border-red-500',
              )}
            >
              {hasRowError ? (
                <p className="px-3 pt-2 text-sm text-destructive" role="alert">
                  {rowErrorMessage}
                </p>
              ) : null}
              <div className="flex items-center gap-2 px-3 py-3">
                <button
                  type="button"
                  className={cn(
                    'inline-flex shrink-0 items-center justify-center',
                    'transition-colors hover:text-primary-600',
                  )}
                  aria-expanded={isOpen}
                  aria-label="展開或收合替代料"
                  onClick={() => toggleItemOpen(key, changeEnabled, hasRowError)}
                >
                  <ChevronDown
                    size={18}
                    className={cn(
                      'shortcut_transition-transform shrink-0 origin-center text-gray-600',
                      isOpen ? 'rotate-0' : '-rotate-90',
                    )}
                    aria-hidden
                  />
                </button>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch
                    checked={changeEnabled}
                    disabled={switchDisabled}
                    aria-label={`${row.sourceGroupMtrName || '組合料'}異動`}
                    onCheckedChange={(checked) => {
                      handleChangeEnabled(index, row, checked === true);
                    }}
                  />
                  <span className="text-xs text-gray-600">異動</span>
                </div>
                <button
                  type="button"
                  className={cn(
                    'min-w-0 flex-1 text-left',
                    'transition-colors hover:text-primary-600',
                  )}
                  aria-expanded={isOpen}
                  onClick={() => toggleItemOpen(key, changeEnabled, hasRowError)}
                >
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <MaterialNameStack
                      name={row.sourceGroupMtrName}
                      code={row.sourceGroupMtrNo || row.sourceGroupMtrId}
                    />
                    {cannotSubstitute ? (
                      <span className="text-xs text-red-500 font-semibold">無可替換</span>
                    ) : null}
                  </div>
                </button>
              </div>
              <input
                type="hidden"
                {...register(`mtrSubstitutions.${index}.sourceGroupMtrId`)}
              />
              <input
                type="hidden"
                {...register(`mtrSubstitutions.${index}.fromMtrId`)}
              />
              <div
                className={cn(
                  'grid shortcut_transition-disclosure',
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
                aria-hidden={!isOpen}
              >
                <div className="min-h-0 overflow-hidden">
                  <div
                    className={cn(
                      'grid grid-cols-1 gap-3 px-3 pb-3',
                      '@2xl/form-section:grid-cols-2',
                      toChanged && 'rounded-md bg-sky-50/60 p-2',
                    )}
                  >
                    <div className="flex min-w-0 flex-col gap-5">
                      <div className="min-w-0">
                        <p className="mb-1 text-xs text-gray-500">目前料</p>
                        <MaterialNameStack
                          name={row.fromMtrName}
                          code={row.fromMtrNo}
                        />
                      </div>
                      <FloatingSelect
                        id={`mtrSubstitutions.${index}.toMtrId`}
                        label="改為"
                        options={options}
                        control={control}
                        errors={errors}
                        isDisabled={contentLocked || (mtrsReady && options.length === 0)}
                        noOptionsMessage="此替代料無可用子料"
                      />
                    </div>
                    {changeEnabled ? (
                      <FloatingTextarea
                        id={`mtrSubstitutions.${index}.remark`}
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={3}
                        containerClassName="mt-2.5 h-full"
                        disabled={contentLocked}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SubstitutionsShell>
  );
};

WorkOrderAmendmentSubstitutionsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  errors: PropTypes.object,
  locked: PropTypes.bool,
};

export default WorkOrderAmendmentSubstitutionsEditor;
